/**
 * L2 — calibrated GO / CONDITIONAL / CONDITIONAL HIGH / NO-GO decision bands.
 */
export type DecisionBandId = 'go' | 'conditional' | 'conditionalHigh' | 'noGo';
export type DecisionBandColor = 'green' | 'amber' | 'orange' | 'red';

/**
 * THE canonical user-facing band labels. Every surface must read them from
 * here — they were previously re-declared in seven separate places, which is
 * how a rename becomes a partial cutover with surfaces disagreeing about the
 * same site.
 *
 * "GO" was replaced (2026-09-03). In investment vocabulary GO reads as an
 * approval, while what the engine actually computed is that the weighted mean
 * of the MEASURED pillars sits in the top band, over at least half the decision
 * frame, with no hard floor from a mandatory source. That is a screening
 * judgement on available evidence, not an engineering sign-off, and "on current
 * evidence" is the part doing the work: it says the judgement can change.
 *
 * The persisted human-decision enum ('GO' | 'CONDITIONAL' | 'NO_GO' | 'DEFER')
 * and the `reports` route id are deliberately NOT renamed — they are data and
 * deep-link identity, not presentation.
 */
export const DECISION_BAND_LABEL: Record<DecisionBandId, string> = {
  go: 'Proceed',
  conditional: 'Proceed with conditions',
  conditionalHigh: 'Proceed with enhanced diligence',
  noGo: 'Do not proceed on current evidence',
};

/**
 * Short forms for space-constrained chrome — map pins, filter chips, dense
 * matrix cells. The full labels are deliberately sentence-length ("Do not
 * proceed on current evidence"), which is right where the reader has room and
 * wrong on a map pin. Same four concepts, one place, so an abbreviation can
 * never drift into meaning something the full label does not.
 */
export const DECISION_BAND_SHORT_LABEL: Record<DecisionBandId, string> = {
  go: 'Proceed',
  conditional: 'Conditions',
  conditionalHigh: 'Diligence',
  noGo: 'Do not proceed',
};

/**
 * The same vocabulary for the DECISION-ANALYSIS band enum, which is a different
 * id-space: `'go' | 'conditional' | 'no-go' | 'withheld'`
 * (decisionAnalysisSystemDecisionV1Schema). It has no `conditionalHigh` and it
 * DOES have `withheld`, so it cannot reuse `DecisionBandId` — but it must not
 * grow a second vocabulary either. The renderer previously printed
 * `band.toUpperCase()`, i.e. the raw enum, which is how "CONDITIONAL" and
 * "NO-GO" survived the Stage 4.1 rename on a live surface.
 *
 * `withheld` reads "Decision pending", matching the label
 * `buildExecutiveDecisionRollup` already returns for the same state, so the two
 * surfaces cannot disagree about what a withheld verdict is called.
 */
export const DECISION_ANALYSIS_BAND_LABEL: Record<
  'go' | 'conditional' | 'no-go' | 'withheld',
  string
> = {
  go: 'Proceed',
  conditional: 'Proceed with conditions',
  'no-go': 'Do not proceed',
  withheld: 'Decision pending',
};

/**
 * P2.3 (2026-09-05) — THE on-screen name for a withheld verdict, whatever
 * withheld it (evidence adequacy, the weight floor, the power-strategy hold).
 * Twelve names used to exist ("Insufficient data", "Insufficient evidence",
 * "Verdict withheld", "NOT COMPUTED" — the last factually wrong: the composite
 * WAS computed, the band was declined). The REASON stays a separate string
 * beside it; "not yet computed" (no composite at all) is a different state and
 * keeps its own name.
 */
export const VERDICT_WITHHELD_LABEL: string = DECISION_ANALYSIS_BAND_LABEL.withheld;

/**
 * Worst → best. Lives here, on the leaf module, because both the source floor
 * and the verdict derivation need to compare two bands and neither may import
 * the other: `sourceFloor` pulls in `mandatoryReports` and `reportVerdict`,
 * which `deriveSiteVerdict` must not transitively depend on.
 */
export const DECISION_BAND_RANK: Record<DecisionBandId, number> = {
  noGo: 0,
  conditionalHigh: 1,
  conditional: 2,
  go: 3,
};

export interface DecisionBand {
  id: DecisionBandId;
  label: string;
  min: number;
  max: number;
  color: DecisionBandColor;
  meaning: string;
}

export interface DecisionBandConfig {
  bands: DecisionBand[];
  assetClass?: string;
}

export const DEFAULT_BANDS: DecisionBand[] = [
  // `meaning` describes the BAND, never the site. The previous GO text —
  // "No material risk barriers" — was an absolute claim about a specific site
  // emitted from a constant attached to a numeric range, with no sight of any
  // pillar, constraint or per-source verdict. Site-specific reasoning now comes
  // from `composeDecisionRationale` in compositeScorer.
  { id: 'go', label: DECISION_BAND_LABEL.go, min: 1.0, max: 2.0, color: 'green', meaning: 'Highest benefit band across the measured decision pillars.' },
  { id: 'conditional', label: DECISION_BAND_LABEL.conditional, min: 2.01, max: 3.0, color: 'amber', meaning: 'Viable with targeted mitigation. Monitor flagged pillars.' },
  { id: 'conditionalHigh', label: DECISION_BAND_LABEL.conditionalHigh, min: 3.01, max: 4.0, color: 'orange', meaning: 'Enhanced diligence required. Executive approval needed.' },
  { id: 'noGo', label: DECISION_BAND_LABEL.noGo, min: 4.01, max: 5.0, color: 'red', meaning: 'Decline. Fundamental barriers cannot be mitigated.' },
];

export const DEFAULT_BAND_CONFIG: DecisionBandConfig = { bands: DEFAULT_BANDS };

const DECISION_SCORE_MIN = 1.0;
const DECISION_SCORE_MAX = 5.0;

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

export function toDecisionScore(benefitComposite: number): number {
  return clamp(6 - benefitComposite, DECISION_SCORE_MIN, DECISION_SCORE_MAX);
}

export function pillarToDecisionScore(pillarBenefitScore: number): number {
  return toDecisionScore(pillarBenefitScore);
}

export function normalizeBandConfig(config?: DecisionBandConfig | null): DecisionBandConfig {
  if (!config?.bands?.length) return DEFAULT_BAND_CONFIG;
  return { assetClass: config.assetClass, bands: config.bands.map(b => ({ ...b })) };
}

export interface BandConfigValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateBandConfig(config: DecisionBandConfig): BandConfigValidationResult {
  const errors: string[] = [];
  const bands = [...config.bands].sort((a, b) => a.min - b.min);
  if (bands.length === 0) return { valid: false, errors: ['At least one band is required.'] };
  if (bands[0].min > DECISION_SCORE_MIN + 0.001) errors.push(`Bands must start at ${DECISION_SCORE_MIN.toFixed(2)}.`);
  if (bands[bands.length - 1].max < DECISION_SCORE_MAX - 0.001) errors.push(`Bands must end at ${DECISION_SCORE_MAX.toFixed(2)}.`);
  for (let i = 0; i < bands.length; i++) {
    const b = bands[i];
    if (b.min > b.max) errors.push(`Band "${b.id}" has min > max.`);
    if (i > 0) {
      const prev = bands[i - 1];
      const gap = b.min - prev.max;
      if (gap > 0.011) errors.push(`Gap between "${prev.id}" and "${b.id}".`);
      if (gap < -0.001) errors.push(`Overlap between "${prev.id}" and "${b.id}".`);
    }
  }
  return { valid: errors.length === 0, errors };
}

export function bandForScore(score: number, config?: DecisionBandConfig | null): DecisionBand {
  const bands = validateBandConfig(normalizeBandConfig(config)).valid
    ? [...normalizeBandConfig(config).bands].sort((a, b) => a.min - b.min)
    : DEFAULT_BANDS;
  const s = clamp(score, DECISION_SCORE_MIN, DECISION_SCORE_MAX);
  for (const band of bands) {
    const isLast = band.id === bands[bands.length - 1]?.id;
    if (s >= band.min && (s <= band.max || (isLast && s <= band.max + 0.001))) return band;
  }
  // BAND-GAP (2026-08-25) — a score can land BETWEEN two bands. The shipped
  // ranges are deliberately non-contiguous (…2.0 | 2.01…, …3.0 | 3.01…) and
  // `validateBandConfig` TOLERATES gaps up to 0.011, but the loop above had no
  // case for a score inside one, so it fell through to the LAST band — the
  // worst one. A decision score of 2.005 (a near-GO site) was published as
  // NO-GO, and the mapping was non-monotonic: 3.0343 → CONDITIONAL HIGH while
  // the strictly BETTER 3.0043 → NO-GO.
  //
  // Snap to the next band UP instead: the conservative neighbour (never
  // optimistic about a site) and monotonic by construction, since `bands` is
  // sorted ascending by `min`. Only a score above every band's max still
  // resolves to the last band, which is the genuine worst-case.
  const nextBandUp = bands.find(band => band.min > s);
  if (nextBandUp) return nextBandUp;
  return bands[bands.length - 1] ?? DEFAULT_BANDS[DEFAULT_BANDS.length - 1];
}

export function bandForBenefitComposite(benefitComposite: number, config?: DecisionBandConfig | null): DecisionBand {
  return bandForScore(toDecisionScore(benefitComposite), config);
}
